import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Camera, Target } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/02c52b1a-551f-4ab4-bb4f-fdecb1df21b7/images/a0af1d125aa03397845c84f2eb1032168b36a0abfab58717dba600a82c5b4921.png";
const PORTRAIT = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/oy887s8a_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2001_30_04%20PM.png";
const VET_ACTION = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/hjuklboh_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2001_33_01%20PM.png";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

const PILLARS = [
  { icon: Heart, title: "Honor Service", desc: "Recognizing the sacrifice and commitment of every veteran who served." },
  { icon: Users, title: "Build Brotherhood", desc: "Creating bonds through shared experiences that transcend service branches." },
  { icon: Camera, title: "Share Stories", desc: "Capturing and amplifying veteran voices through powerful media content." },
  { icon: Target, title: "Drive Impact", desc: "Every experience, every story, every partnership drives measurable change." },
];

export default function MissionPage() {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/veteran-stories`).then(r => setStories(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="mission-page">
      {/* ── HERO ── */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            poster="https://static.prod-images.emergentagent.com/jobs/02c52b1a-551f-4ab4-bb4f-fdecb1df21b7/images/a0af1d125aa03397845c84f2eb1032168b36a0abfab58717dba600a82c5b4921.png"
            className="w-full h-full object-cover"
          >
            <source src="/mission_bg.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center py-32">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold mb-4">Our Mission</p>
          </motion.div>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-8"
          >
            Purpose Beyond<br/>Entertainment
          </motion.h1>
          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-body text-base lg:text-lg text-zinc-300 max-w-2xl mx-auto leading-relaxed"
          >
            Our mission is to provide veterans with unforgettable, once-in-a-lifetime experiences 
            while capturing and sharing their stories to inspire others.
          </motion.p>
        </div>
      </section>

      {/* ── WHY VETERANS MATTER ── */}
      <section className="py-24 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">The Why</p>
              <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-tight mb-6">
                Why Veterans Matter
              </h2>
              <p className="font-body text-base lg:text-lg text-zinc-400 leading-relaxed mb-6">
                Every veteran carries stories of sacrifice, courage, and resilience that most people 
                will never understand. They've faced situations that test the limits of human endurance 
                and come out the other side.
              </p>
              <p className="font-body text-base text-zinc-500 leading-relaxed mb-6">
                But the transition home is often where the hardest battle begins. Isolation, loss of 
                purpose, and a civilian world that can't relate. Bad Company exists to bridge that gap - 
                to remind veterans that their service matters, their stories matter, and they deserve 
                experiences that celebrate who they are.
              </p>
              <div className="flex gap-12 mt-8">
                <div>
                  <span className="font-heading text-4xl">18M+</span>
                  <p className="font-body text-xs uppercase tracking-wider text-zinc-500 mt-1">US Veterans</p>
                </div>
                <div>
                  <span className="font-heading text-4xl">200K+</span>
                  <p className="font-body text-xs uppercase tracking-wider text-zinc-500 mt-1">Transitioning Yearly</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="overflow-hidden border border-white/10">
                <img src={VET_ACTION} alt="Veterans" className="w-full aspect-[4/5] object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── PILLARS ── */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Our Pillars</p>
            <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight">
              How We Create Impact
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="border border-white/10 p-8 card-hover"
              >
                <div className="w-14 h-14 border border-white/20 flex items-center justify-center mb-6">
                  <pillar.icon size={24} className="text-zinc-400" />
                </div>
                <h3 className="font-subheading text-lg uppercase tracking-normal mb-3">{pillar.title}</h3>
                <p className="font-body text-sm text-zinc-500 leading-relaxed">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EMOTIONAL BLOCK ── */}
      <section className="py-24 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="overflow-hidden border border-white/10">
                <img src={PORTRAIT} alt="Veteran portrait" className="w-full aspect-square object-cover" />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <blockquote className="border-l-2 border-white/20 pl-8">
                <p className="font-subheading text-2xl sm:text-3xl uppercase tracking-tight leading-tight mb-6 text-zinc-200">
                  "The day I thought nobody remembered us, Bad Company showed up and reminded me 
                  why I served in the first place."
                </p>
                <footer className="font-body text-sm text-zinc-500">
                  &mdash; Veteran Participant
                </footer>
              </blockquote>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── VETERANS WE'VE SERVED ── */}
      {stories.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Impact</p>
              <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-4">
                Veterans We've Served
              </h2>
              <p className="font-body text-base text-zinc-500 max-w-xl mx-auto">
                Real veterans. Real impact. Here are some of the heroes we've had the honor of serving.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {stories.map((story, i) => (
                <motion.div
                  key={story.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="border border-white/10 overflow-hidden card-hover"
                  data-testid={`veteran-story-${i}`}
                >
                  {story.image_url && (
                    <div className="aspect-square overflow-hidden">
                      <img src={story.image_url} alt={story.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-subheading text-lg uppercase tracking-wider mb-2">{story.name}</h3>
                    <p className="font-body text-sm text-zinc-500 leading-relaxed">{story.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl uppercase tracking-tighter leading-[0.9] mb-6">
            Be Part Of<br/>The Mission
          </h2>
          <p className="font-body text-base text-zinc-500 mb-10 max-w-lg mx-auto">
            Every contribution, every nomination, every share amplifies the impact.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/get-involved"
              data-testid="mission-cta-get-involved"
              className="font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors duration-300 flex items-center gap-2"
            >
              Get Involved <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="mission-cta-contact"
              className="font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-8 py-3.5 hover:border-white hover:bg-white/5 transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
